import { useId, useState } from "react";

/**
 * Charts for the usage pages, drawn as plain SVG.
 *
 * No chart library: each of these is one series over a handful of points, and a
 * dependency would cost more in bundle than it saves in code. What matters is that they
 * follow the house data-viz rules rather than a library's defaults — thin marks, a
 * recessive grid, selective labels (never a number on every point), a hover layer on
 * every plot, and colours that are *selected* for each mode rather than flipped.
 *
 * Colour roles, from the design system:
 * - one series → the categorical slot 1 blue (`#2a78d6` light / `#3987e5` dark). A single
 *   series needs no legend; the chart title names it.
 * - status → the fixed status palette (good / warning / critical), and every status bar
 *   carries its code as a label, because colour must never be the only channel.
 */

const CHART_STYLE = `
.viz {
  --series-1: #2a78d6;
  --status-good: #0ca30c;
  --status-warning: #fab219;
  --status-critical: #d03b3b;
  --viz-grid: #e1e0d9;
  --viz-axis: #c3c2b7;
  --viz-muted: #898781;
  --viz-surface: #fcfcfb;
}
@media (prefers-color-scheme: dark) {
  :root:where(:not([data-theme="light"])) .viz {
    --series-1: #3987e5;
    --viz-grid: #2c2c2a;
    --viz-axis: #383835;
    --viz-surface: #1a1a19;
  }
}
:root[data-theme="dark"] .viz {
  --series-1: #3987e5;
  --viz-grid: #2c2c2a;
  --viz-axis: #383835;
  --viz-surface: #1a1a19;
}
.viz text { fill: var(--viz-muted); font-size: 11px; }
`;

/** Injected once per page rather than per chart. */
export function VizStyle() {
  return <style>{CHART_STYLE}</style>;
}

function formatNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

function niceMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return Math.ceil(value / magnitude) * magnitude;
}

export interface TimePoint {
  timestamp: string;
  requests: number;
}

/**
 * Requests over time.
 *
 * One series, so no legend — and no marker on every point either: at 90 days that is 90
 * dots competing with the line they belong to. The hover layer is what makes an
 * individual value readable, which is why it is not optional.
 */
/** `daily`: one point per day, so the tooltip shows the date without a clock time. */
export function RequestsOverTime({ points, daily = false }: { points: TimePoint[]; daily?: boolean }) {
  const [hover, setHover] = useState<number | null>(null);
  const clipId = useId();

  if (points.length === 0) return <EmptyPlot />;

  const width = 720;
  const height = 220;
  const pad = { top: 12, right: 12, bottom: 28, left: 44 };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;

  const max = niceMax(Math.max(...points.map((p) => p.requests)));
  const x = (index: number) =>
    pad.left + (points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
  const y = (value: number) => pad.top + plotHeight - (value / max) * plotHeight;

  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.requests)}`).join(" ");
  const ticks = [0, max / 2, max];

  return (
    <div className="viz relative">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        role="img"
        aria-label={`Requests over time, ${points.length} points, peak ${max}`}
        onMouseLeave={() => setHover(null)}
      >
        <clipPath id={clipId}>
          <rect x={pad.left} y={pad.top} width={plotWidth} height={plotHeight} />
        </clipPath>

        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={y(tick)}
              y2={y(tick)}
              stroke="var(--viz-grid)"
              strokeWidth={1}
            />
            <text x={pad.left - 8} y={y(tick) + 4} textAnchor="end">
              {formatNumber(tick)}
            </text>
          </g>
        ))}

        <path
          d={path}
          fill="none"
          stroke="var(--series-1)"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          clipPath={`url(#${clipId})`}
        />

        {hover !== null && (
          <g>
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={pad.top}
              y2={pad.top + plotHeight}
              stroke="var(--viz-axis)"
              strokeWidth={1}
            />
            {/* The 2px surface ring keeps the marker legible where it sits on the line. */}
            <circle
              cx={x(hover)}
              cy={y(points[hover].requests)}
              r={4}
              fill="var(--series-1)"
              stroke="var(--viz-surface)"
              strokeWidth={2}
            />
          </g>
        )}

        {/* Hit targets are far wider than the marks, so hovering is not a precision task. */}
        {points.map((point, index) => (
          <rect
            key={point.timestamp}
            x={x(index) - plotWidth / points.length / 2}
            y={pad.top}
            width={Math.max(plotWidth / points.length, 4)}
            height={plotHeight}
            fill="transparent"
            onMouseEnter={() => setHover(index)}
          />
        ))}

        <line
          x1={pad.left}
          x2={width - pad.right}
          y1={pad.top + plotHeight}
          y2={pad.top + plotHeight}
          stroke="var(--viz-axis)"
        />
        <text x={pad.left} y={height - 8}>
          {new Date(points[0].timestamp).toLocaleDateString()}
        </text>
        <text x={width - pad.right} y={height - 8} textAnchor="end">
          {new Date(points[points.length - 1].timestamp).toLocaleDateString()}
        </text>
      </svg>

      {hover !== null && (
        <Tooltip
          label={daily ? points[hover].timestamp.slice(0, 10) : new Date(points[hover].timestamp).toLocaleString()}
          value={`${points[hover].requests.toLocaleString()} requests`}
        />
      )}
    </div>
  );
}

export interface NamedCount {
  name: string;
  requests: number;
  failed?: number;
}

/**
 * Requests by endpoint — horizontal bars, because endpoint names are long text and
 * horizontal is the only orientation that lets them be read without rotating.
 */
export function RequestsByEndpoint({ rows }: { rows: NamedCount[] }) {
  const [hover, setHover] = useState<number | null>(null);
  if (rows.length === 0) return <EmptyPlot />;

  const max = Math.max(...rows.map((r) => r.requests));

  return (
    <div className="viz relative space-y-1">
      {rows.map((row, index) => (
        <div
          key={row.name}
          className="grid grid-cols-[minmax(0,14rem)_1fr_auto] items-center gap-3 text-xs"
          onMouseEnter={() => setHover(index)}
          onMouseLeave={() => setHover(null)}
        >
          <span className="truncate font-mono text-muted-foreground" title={row.name}>
            {row.name}
          </span>
          <div className="h-4 w-full">
            <div
              className="h-full rounded-r-[4px]"
              style={{
                width: `${Math.max((row.requests / max) * 100, 0.5)}%`,
                background: "var(--series-1)",
              }}
            />
          </div>
          <span className="tabular-nums text-muted-foreground">
            {row.requests.toLocaleString()}
            {row.failed ? ` · ${row.failed} failed` : ""}
          </span>
        </div>
      ))}
      {hover !== null && rows[hover].failed !== undefined && (
        <Tooltip
          label={rows[hover].name}
          value={`${rows[hover].requests.toLocaleString()} requests · ${rows[hover].failed} failed`}
        />
      )}
    </div>
  );
}

export interface StatusCount {
  resultCode: string;
  requests: number;
}

/** Which status class a code belongs to. `0` and empty are how AI records a dropped call. */
function statusRole(code: string): "good" | "warning" | "critical" | "muted" {
  const first = String(code).trim()[0];
  if (first === "2" || first === "3") return "good";
  if (first === "4") return "warning";
  if (first === "5") return "critical";
  return "muted";
}

const ROLE_COLOR: Record<ReturnType<typeof statusRole>, string> = {
  good: "var(--status-good)",
  warning: "var(--status-warning)",
  critical: "var(--status-critical)",
  muted: "var(--viz-axis)",
};

/**
 * Requests by status code.
 *
 * Status colours are the reserved palette, never a series hue — and every bar carries its
 * code as a visible label, because `warning` is deliberately sub-3:1 on the light surface
 * and colour alone must never be what tells an operator a class apart.
 */
export function RequestsByStatus({ rows }: { rows: StatusCount[] }) {
  if (rows.length === 0) return <EmptyPlot />;
  const max = Math.max(...rows.map((r) => r.requests));

  return (
    <div className="viz space-y-1">
      {rows.map((row) => (
        <div
          key={row.resultCode}
          className="grid grid-cols-[3.5rem_1fr_auto] items-center gap-3 text-xs"
        >
          <span className="font-mono tabular-nums">{row.resultCode || "—"}</span>
          <div className="h-4 w-full">
            <div
              className="h-full rounded-r-[4px]"
              style={{
                width: `${Math.max((row.requests / max) * 100, 0.5)}%`,
                background: ROLE_COLOR[statusRole(row.resultCode)],
              }}
            />
          </div>
          <span className="tabular-nums text-muted-foreground">
            {row.requests.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

function Tooltip({ label, value }: { label: string; value: string }) {
  return (
    <div className="pointer-events-none absolute right-2 top-2 rounded-md border bg-popover px-2 py-1 text-xs shadow-sm">
      <div className="font-medium">{label}</div>
      <div className="text-muted-foreground">{value}</div>
    </div>
  );
}

function EmptyPlot() {
  return (
    <p className="py-8 text-center text-sm text-muted-foreground">
      No requests matched these filters in this range.
    </p>
  );
}
